import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgHeartFilled from '@icons/heart-filled.svg?react';
import SvgHeart from '@icons/heart.svg?react';
import SvgSave from '@icons/save.svg?react';
import SvgReset from '@icons/reset-filled.svg?react';
import { FilterSort } from '@hooks/useFilter/Filter.tsx';
import { FilterConfig, SortableColumn } from '../types';
import { FilterChipControl } from './FilterChipControl';
import { SortChip } from './SortChip';
import { getFilterKey, getFilterLabel } from './filterChipUtils';

// Sort + filter row labels: 14/20 medium, content-primary, ellipsis on overflow.
// Font family is inherited from the DM Sans wrapper below — the app never loads Inter.
const LABEL_CLASS = 'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-5 text-content-primary';

interface Props {
  visible: boolean;
  onClose: () => void;
  filtersConf: FilterConfig[];
  filter: any;
  applyFilterValue: (key: string, value: any) => void;
  loading: boolean;
  isHearted: (fieldKey: string) => boolean;
  toggleHearted: (fieldKey: string) => void;
  onReset: () => void;
  onSaveFilter: () => void;
  savingEnabled: boolean;
  sort?: FilterSort;
  applySorting?: (sortField: string, sortDirection: 'ASC' | 'DESC') => void;
  sortableColumns?: SortableColumn[];
  /** Replaces the "All filters" heading (v3: "Default filters" of a tab). */
  heading?: string;
  /** A line under the heading, e.g. the name of the tab whose defaults are edited. */
  subheading?: string;
}

export const AllFiltersSidebar: React.FC<Props> = ({
  visible,
  onClose,
  filtersConf,
  filter,
  applyFilterValue,
  loading,
  isHearted,
  toggleHearted,
  onReset,
  onSaveFilter,
  savingEnabled,
  sort,
  applySorting,
  sortableColumns,
  heading,
  subheading,
}) => {
  const { tr } = useTranslationLgs();

  return (
    <RingilSidebar
      visible={visible}
      heading={
        subheading ? (
          <span className="flex flex-col gap-1">
            <span>{heading ?? tr('RingilDataTable.allFilters', 'All filters')}</span>
            <span className="text-sm font-medium text-content-primary">{subheading}</span>
          </span>
        ) : (
          heading ?? tr('RingilDataTable.allFilters', 'All filters')
        )
      }
      onBack={onClose}
      onContinue={onClose}
      continueLabel={tr('RingilDataTable.applyFilter', 'Filter')}
      showSaveButton
      showBackButton
      maxWidth="520px"
    >
      {/* Sort + all filters in a padded flex column; the white background sits on each row, not the column. */}
      <div className="font-['DM_Sans'] flex flex-col items-start self-stretch gap-0.5 p-3">
        {/* Sort row — reuses the same SortChip as the filter bar. mb-1.5 so the gap to the first filter is 16px. */}
        {applySorting && sortableColumns && sortableColumns.length > 0 && (
          <div className="flex w-full items-center justify-between bg-white rounded px-3 py-2 mb-3.5">
            <span className={LABEL_CLASS}>{tr('RingilDataTable.sort', 'Sort')}</span>
            <SortChip sort={sort} applySorting={applySorting} sortableColumns={sortableColumns} />
          </div>
        )}

        {/* One row per filterable field — same chips as the filter bar. */}
        {filtersConf.map(conf => (
          <div key={getFilterKey(conf)} className="flex w-full items-center justify-between gap-2 bg-white rounded px-3 py-2">
            <button
              type="button"
              onClick={() => toggleHearted(getFilterKey(conf))}
              className="inline-flex min-w-0 items-center gap-2 bg-transparent border-none cursor-pointer text-left"
              title={tr('RingilDataTable.pinToDefault', 'Show in the default filter set')}
              data-testid={`heart-${getFilterKey(conf)}`}
            >
              {isHearted(getFilterKey(conf)) ? (
                <SvgHeartFilled className="w-4 h-4 shrink-0 text-content-brand" />
              ) : (
                <SvgHeart className="w-4 h-4 shrink-0 text-content-brand" />
              )}
              <span className={LABEL_CLASS}>{getFilterLabel(conf)}</span>
            </button>
            <FilterChipControl conf={conf} filter={filter} applyFilterValue={applyFilterValue} loading={loading} wrap hideLabel />
          </div>
        ))}
      </div>

      {/* Reset + Save — link buttons, same style as the filter bar. The primary Filter/Back actions come
          from RingilSidebar's own footer. */}
      <div className="flex items-center gap-2 px-3 pt-2">
        <RingilButton
          mode="link"
          label={tr('RingilDataTable.resetFilter', 'Reset')}
          icon={<SvgReset className="w-3.5 h-3.5" />}
          onClick={onReset}
          className="!text-complementary-error hover:!text-content-brand"
          dataTestId="all-filters-reset"
        />
        {savingEnabled && (
          <RingilButton
            mode="link"
            label={tr('RingilDataTable.saveFilter', 'Save')}
            icon={<SvgSave className="w-4 h-4" />}
            onClick={onSaveFilter}
            className="ml-auto"
            dataTestId="all-filters-save"
          />
        )}
      </div>
    </RingilSidebar>
  );
};
